import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AcademicCapIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ClipboardDocumentCheckIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import LearningOrderModal from '../components/LearningOrderModal';
import ManageTrackedWordbooksModal from '../components/ManageTrackedWordbooksModal';
import { playSoundEffect } from '../lib/sfx';
import { useLocation } from 'react-router-dom';
import { DEFAULT_TIER, TIER_INFO, getAvailableTiers } from '../lib/learningTiers';
import {
  buildPracticeLaunchUrl,
  isPracticeStartDisabled,
  normalizeRequestedCount,
  pendingPracticeAssignments,
  selectTeacherAssignments,
} from '../lib/practiceAssignments';
import { buildIeltsSpeakingLaunchUrl } from '../lib/ieltsSpeaking';
import TrainingModeSelector, { TrainingModeBadge } from '../components/TrainingModeSelector';
import CustomReviewSelector from '../components/CustomReviewSelector';
import {
  TRAINING_MODE_COMPREHENSIVE,
  TRAINING_MODE_MEANING,
  countTrainingModes,
  getAvailableTrainingModeCounts,
  getTrainingMode,
  normalizeTrainingMode,
} from '../lib/trainingModes';
import { clearCustomReviewSelection, writeCustomReviewSelection } from '../lib/customReview';
import { useI18n } from '../i18n/I18nProvider';
import { AUTH_EXPIRED_EVENT } from '../setupFetch';

const StudentDashboard = () => {
  const { t } = useI18n();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState('');
  const [wordbooks, setWordbooks] = useState([]);
  const [selectedWordbookId, setSelectedWordbookId] = useState('');
  const [learnCount, setLearnCount] = useState(10);
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedTier, setSelectedTier] = useState('');
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewWords, setPreviewWords] = useState([]);
  const [previewError, setPreviewError] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewMeta, setPreviewMeta] = useState({ wbId: '', count: 0 });
  const navigate = useNavigate();
  const location = useLocation();
  // Create/manage wordbooks moved to /student/my-wordbooks
  const [streak, setStreak] = useState(0);
  const [showTbm, setShowTbm] = useState(false);
  const [showOrder, setShowOrder] = useState(false);
  const [showTracked, setShowTracked] = useState(false);

  const handleSessionExpired = () => {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    navigate('/login', { replace: true });
  };
  const [todayLearned, setTodayLearned] = useState(0);
  const [hasSecret, setHasSecret] = useState(false); // kept for compatibility; prefer summary.has_secret when available
  const useSecretMode = (summary?.has_secret === true && summary?.secret_wordbook_completed !== true);
  const displayLearned = useSecretMode
    ? (typeof summary?.secret_today_learned === 'number' ? summary.secret_today_learned : 0)
    : todayLearned;
  const remainingLearn = Math.max(0, (summary?.learning_goal || 0) - (Number.isFinite(displayLearned) ? displayLearned : 0));
  // UI 鍏滃簳锛氳嫢浠婃棩瀛︿範鏁板凡杈炬垚鐩爣锛屽垯瑙嗕负 Achieved锛堥伩鍏嶅悗绔爣蹇楀欢杩熷鑷磋鍒わ級
  const uiGoalMet = useMemo(() => {
    const goal = Number(summary?.learning_goal || 0);
    const learned = (typeof summary?.secret_today_learned === 'number')
      ? Number(summary.secret_today_learned)
      : Number(todayLearned || 0);
    if (summary?.goal_today_met === true) return true;
    if (goal <= 0) return false;
    return learned >= goal;
  }, [summary, todayLearned]);
  const [reviewPlan, setReviewPlan] = useState(null);
  const [reviewBatchCount, setReviewBatchCount] = useState(1);
  const [reviewGoalDraft, setReviewGoalDraft] = useState(10);
  const [reviewGoalSaving, setReviewGoalSaving] = useState(false);
  const [priorityWordbookId, setPriorityWordbookId] = useState('');
  const [priorityWords, setPriorityWords] = useState([]);
  const [priorityTitle, setPriorityTitle] = useState('');
  const [priorityLoading, setPriorityLoading] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const [quickCount, setQuickCount] = useState(0);
  const [quickWbId, setQuickWbId] = useState('');
  const [quickAssigning, setQuickAssigning] = useState(false);
  const [quickTier, setQuickTier] = useState('');
  const [quickMax, setQuickMax] = useState(0);
  const [quickMaxLoading, setQuickMaxLoading] = useState(false);
  const [showReviewQuick, setShowReviewQuick] = useState(false);
  const [showCustomReview, setShowCustomReview] = useState(false);
  const [reviewTier, setReviewTier] = useState('');
  const [reviewTrainingMode, setReviewTrainingMode] = useState(TRAINING_MODE_MEANING);
  // Learn teacher-assigned words now
  const [showTeacherLearn, setShowTeacherLearn] = useState(false);
  const [teacherLearnTier, setTeacherLearnTier] = useState('');
  const [teacherLearnCount, setTeacherLearnCount] = useState(1);
  const [teacherLearnMode, setTeacherLearnMode] = useState(TRAINING_MODE_MEANING);
  const [invites, setInvites] = useState([]);
  const [liveStats, setLiveStats] = useState(null);
  // To-Do: pending quizzes
  const [quizLoading, setQuizLoading] = useState(true);
  const [quizError, setQuizError] = useState('');
  const [pendingQuizzes, setPendingQuizzes] = useState([]);
  const [pendingPractices, setPendingPractices] = useState([]);
  const [startingPracticeId, setStartingPracticeId] = useState('');

  // One-click learning top-up flow
  const EXISTING_KEY = '__existing__';
  const [topUpVisible, setTopUpVisible] = useState(false);
  const [topUpTarget, setTopUpTarget] = useState(0);
  const [topUpNeeded, setTopUpNeeded] = useState(0);
  const [topUpWords, setTopUpWords] = useState([]);
  const [topUpPlan, setTopUpPlan] = useState({}); // wbId -> planned words
  const [topUpSelectedWbId, setTopUpSelectedWbId] = useState('');
  const [initialAssignWbId, setInitialAssignWbId] = useState('');
  const [oneClickTier, setOneClickTier] = useState('');
  const [topUpAssigning, setTopUpAssigning] = useState(false); // confirming start
  const [topUpLoading, setTopUpLoading] = useState(false); // previewing to fill
  // One-click learning: backend cached plan
  const [oneClickPlannedWords, setOneClickPlannedWords] = useState([]);
  const [oneClickPlanTitle, setOneClickPlanTitle] = useState('');
  const [oneClickPlanCounts, setOneClickPlanCounts] = useState({ base_count: 0, supplement_count: 0 });
  const [showOneClickPreview, setShowOneClickPreview] = useState(false);

  // Tier options aligned with WordPracticePage
  const availableTiers = useMemo(() => {
    return getAvailableTiers(summary?.minimum_tier || DEFAULT_TIER);
  }, [summary?.minimum_tier]);
  const teacherAssignments = useMemo(() => {
    if (Array.isArray(summary?.teacher_assigned_items)) {
      return summary.teacher_assigned_items.filter((item) => item?.word);
    }
    return Array.isArray(summary?.teacher_assigned)
      ? summary.teacher_assigned.filter(Boolean).map((value) => (
        typeof value === 'string' ? { word: value, sense_id: 'primary' } : value
      ))
      : [];
  }, [summary?.teacher_assigned, summary?.teacher_assigned_items]);
  const reviewModeCounts = useMemo(
    () => getAvailableTrainingModeCounts(reviewPlan),
    [reviewPlan],
  );
  const teacherModeCounts = useMemo(() => countTrainingModes(teacherAssignments), [teacherAssignments]);
  const labelByTier = Object.fromEntries(Object.entries(TIER_INFO).map(([id, info]) => [id, info.studentLabel]));

  // Ensure selected quick/one-click tier always within allowed range
  useEffect(() => {
    if (showQuick) {
      const allowed = availableTiers;
      if (!allowed.includes(quickTier)) setQuickTier(allowed[0] || DEFAULT_TIER);
    }
  }, [showQuick, availableTiers, quickTier]);
  useEffect(() => {
    if (topUpVisible) {
      const allowed = availableTiers;
      if (!allowed.includes(oneClickTier)) setOneClickTier(allowed[0] || DEFAULT_TIER);
    }
  }, [topUpVisible, availableTiers, oneClickTier]);

  // Initialize teacher-learn tier when opening modal
  useEffect(() => {
    if (showTeacherLearn) {
      const allowed = availableTiers;
      if (!allowed.includes(teacherLearnTier)) setTeacherLearnTier(allowed[0] || DEFAULT_TIER);
      setTeacherLearnCount((current) => {
        const total = teacherAssignments.length;
        if (total === 0) return 1;
        return current > 0 && current <= total ? current : total;
      });
    }
  }, [showTeacherLearn, availableTiers, teacherLearnTier, teacherAssignments.length]);

  const loadPriorityWords = async (wbId) => {
    setPriorityWords([]);
    if (!wbId) return;
    try {
      setPriorityLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/student/wordbooks/${wbId}/tbm-words`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(()=>({}));
      if (res.ok) {
        setPriorityWords(Array.isArray(data.words) ? data.words : []);
        setPriorityTitle(data?.wordbook_title || '');
      } else {
        setPriorityTitle('');
      }
    } catch (_) {
      // ignore
    } finally {
      setPriorityLoading(false);
    }
  };
  

  const fetchSummary = async () => {
    const token = localStorage.getItem('token');
    const res = await fetch('/api/student/dashboard-summary', { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      const s = await res.json();
      setSummary(s);
      if (s?.review_plan) setReviewPlan(s.review_plan);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const beijingTime = new Date().toLocaleString('en-US', { timeZone: 'Asia/Shanghai' });
      setCurrentTime(new Date(beijingTime).toLocaleString());
    }, 1000);

    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        // Trigger backend to refresh today's stats before reading them
        try { await fetch('/api/student/stats', { headers: { Authorization: `Bearer ${token}` } }); } catch (_) {}
        const [summaryRes, wordbooksRes, statsRes, trackedRes, reviewRes, inviteRes, compactRes] = await Promise.all([
          fetch('/api/student/dashboard-summary', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/student/wordbooks', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/student/study-stats?days=30', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/student/tracked-wordbooks', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/student/review-plan', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/student/invitations', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/student/stats', { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (!summaryRes.ok) throw new Error('Failed to fetch learning data');
        const s = await summaryRes.json();
        setSummary(s);
        if (wordbooksRes.ok) {
          const list = await wordbooksRes.json();
          let books = Array.isArray(list) ? list : [];
          if (trackedRes.ok) {
            const tr = await trackedRes.json().catch(()=>({ ids: [] }));
            const ids = Array.isArray(tr.ids) ? tr.ids : [];
            if (ids.length > 0) {
              // Always include own private wordbooks, plus tracked public books
              const priv = books.filter(b => (b && b.accessibility === 'private'));
              const tracked = books.filter(b => ids.includes(b._id));
              const merged = {};
              for (const b of [...priv, ...tracked]) { if (b && b._id) merged[b._id] = b; }
              books = Object.values(merged);
            }
          }
          setWordbooks(books);
          if (!selectedWordbookId && books.length > 0) setSelectedWordbookId(books[0]._id);
        }
        if (inviteRes && inviteRes.ok) {
          const list = await inviteRes.json().catch(()=>[]);
          setInvites(Array.isArray(list) ? list : []);
        }
        // 鍙栨秷 preflight/onboarding锛氫笉鍐嶅脊棣栫櫥寮曞
        if (statsRes.ok) {
          const stats = await statsRes.json();
          setStreak(stats?.goal_streak_days || 0);
          const arr = Array.isArray(stats?.by_day) ? stats.by_day : [];
          const today = arr.length > 0 ? arr[arr.length - 1] : null;
          setTodayLearned(today?.learned || 0);
          // prefer summary.has_secret; keep this as a fallback signal
          setHasSecret(Boolean(stats?.has_secret));
        }
        if (compactRes && compactRes.ok) {
          const comp = await compactRes.json().catch(()=>null);
          if (comp) setLiveStats(comp);
        }
        if (reviewRes.ok) {
          const plan = await reviewRes.json().catch(() => null);
          if (plan) {
            setReviewPlan(plan);
            setReviewGoalDraft(Number(plan.daily_review_goal || 10));
            setReviewBatchCount(Math.max(1, Number(plan.default_batch_count || 1)));
          }
        }
        // Initialize tier select from profile
        if (!selectedTier) setSelectedTier(s?.default_tier || s?.tier || DEFAULT_TIER);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    return () => clearInterval(timer);
  }, []);

  // 杩斿洖椤甸潰鎴栨爣绛鹃〉鍙鏃惰嚜鍔ㄥ埛鏂?summary 涓?stats锛屼繚璇?Today鈥檚 Status 鍙婃椂鏇存柊
  useEffect(() => {
    const handleRefresh = async () => {
      try {
        const token = localStorage.getItem('token');
        // Fetch compact stats (also triggers backend recompute) and update UI
        try {
          const cr = await fetch('/api/student/stats', { headers: { Authorization: `Bearer ${token}` } });
          if (cr.ok) { const c = await cr.json(); setLiveStats(c); }
        } catch (_) {}
        await fetchSummary();
        const statsRes = await fetch('/api/student/study-stats?days=30', { headers: { Authorization: `Bearer ${token}` } });
        if (statsRes.ok) {
          const stats = await statsRes.json();
          setStreak(stats?.goal_streak_days || 0);
          const arr = Array.isArray(stats?.by_day) ? stats.by_day : [];
          const today = arr.length > 0 ? arr[arr.length - 1] : null;
          setTodayLearned(today?.learned || 0);
          setHasSecret(Boolean(stats?.has_secret));
        }
      } catch (_) {}
    };
    const onVisibility = () => { if (!document.hidden) handleRefresh(); };
    window.addEventListener('focus', handleRefresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', handleRefresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Load legacy quizzes and unified assigned practice independently.
  useEffect(() => {
    const loadToDo = async () => {
      setQuizLoading(true); setQuizError('');
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const legacyRequest = async () => {
        const profRes = await fetch('/api/user/profile', { headers });
        const profile = await profRes.json();
        if (!profRes.ok || !profile?.username) throw new Error();
        const progressRes = await fetch(`/api/progress/${encodeURIComponent(profile.username)}`, { headers });
        const progress = await progressRes.json();
        if (!progressRes.ok) throw new Error();
        return (Array.isArray(progress) ? progress : [])
          .filter(q => q.status === 'pending' && (!q.publish_status || q.publish_status === 'published'));
      };
      const practiceRequest = async () => {
        const response = await fetch('/api/student/practice-assignments', { headers });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error();
        return pendingPracticeAssignments(data.practices);
      };
      try {
        const [legacyResult, practiceResult] = await Promise.allSettled([legacyRequest(), practiceRequest()]);
        setPendingQuizzes(legacyResult.status === 'fulfilled' ? legacyResult.value : []);
        setPendingPractices(practiceResult.status === 'fulfilled' ? practiceResult.value : []);
        if (legacyResult.status === 'rejected' && practiceResult.status === 'rejected') {
          setQuizError('Could not load your assigned practice.');
        } else if (legacyResult.status === 'rejected' || practiceResult.status === 'rejected') {
          setQuizError('Some assigned practice could not be loaded.');
        }
      } finally {
        setQuizLoading(false);
      }
    };
    loadToDo();
  }, []);

  const startAssignedPractice = async (item) => {
    const assignmentId = item?.assignment?.id;
    if (!assignmentId || startingPracticeId || isPracticeStartDisabled(item.status)) return;
    if (item?.assignment?.practice_type === 'ielts_speaking') {
      navigate(buildIeltsSpeakingLaunchUrl({
        payload: {
          assignment_id: assignmentId,
          target_id: item?.id || '',
        },
      }));
      return;
    }
    setStartingPracticeId(assignmentId);
    setQuizError('');
    try {
      const response = await fetch(`/api/student/practice-assignments/${assignmentId}/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.code === 'practice.not_available_yet'
          ? 'This practice opens later.'
          : 'The practice could not be opened.');
      }
      navigate(buildPracticeLaunchUrl(data.launch));
    } catch (startError) {
      setQuizError(startError.message || 'The practice could not be opened.');
    } finally {
      setStartingPracticeId('');
    }
  };

  // Support opening quick-assign modal via URL param: /student/dashboard?openQuick=1
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || window.location.search || '');
      const openQuick = params.get('openQuick');
      if (openQuick === '1') {
        const firstId = selectedWordbookId || (wordbooks[0]?._id || '');
        setQuickWbId(firstId);
        setQuickCount(0);
        setQuickTier(selectedTier || summary?.default_tier || summary?.tier || DEFAULT_TIER);
        setShowQuick(true);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, wordbooks, selectedWordbookId]);

  // 缁熻瀛︾敓宸叉帉鎻?寰呮帉鎻¤瘝锛岀敤浜庤繃婊わ紙闇€鍦ㄤ换浣?early return 涔嬪墠澹版槑 hooks锛?
  const excludeWordsSet = useMemo(() => {
    const set = new Set();
    const tbm = summary?.to_be_mastered || [];
    const wm = summary?.words_mastered || [];
    tbm.forEach(x => { const w = (typeof x === 'string' ? x : x.word); if (w) set.add(w); });
    wm.forEach(x => { const w = (typeof x === 'string' ? x : x.word); if (w) set.add(w); });
    return set;
  }, [summary]);

  // 褰撶敤鎴锋洿鎹⑩€滆ˉ榻愯瘝搴撯€濇椂锛氶噸鏂扮敓鎴愪竴娆″涔犺鍒掑苟缂撳瓨锛堜粎棰勮锛屼笉鍐欏叆锛?
  useEffect(() => {
    const rebuildPlan = async () => {
      if (!topUpVisible) return;
      if (!topUpSelectedWbId) return;
      const count = topUpTarget || 0;
      if (count <= 0) return;
      try {
        setTopUpLoading(true);
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/student/learning-plan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ wordbook_id: topUpSelectedWbId, count })
        });
        const data = await res.json().catch(()=>({}));
        if (res.ok) {
          const words = Array.isArray(data?.words) ? data.words : [];
          setOneClickPlannedWords(words);
          setOneClickPlanTitle(data?.wordbook_title || 'Selected Wordbook');
          setOneClickPlanCounts({ base_count: Number(data?.base_count||0), supplement_count: Number(data?.supplement_count||0) });
        }
      } catch (_) {
        // ignore
      } finally {
        setTopUpLoading(false);
      }
    };
    rebuildPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topUpSelectedWbId]);

  // 璁＄畻鈥滅珛鍗冲涔犫€濈殑鏈€澶у彲瀛︽暟閲忥細寰呮帉鎻℃暟閲?+ 鎵€閫夎瘝搴撳彲琛ラ綈鏁伴噺
  useEffect(() => {
    const calcQuickMax = async () => {
      if (!showQuick) return;
      // 寰呮帉鎻★紙鑰佸笀浼樺厛 + 鍏跺畠锛夋€婚噺
      const teacherConnected = summary?.has_teacher === true;
      let baseAvailable = 0;
      if (teacherConnected) {
        const tbm = Array.isArray(summary?.to_be_mastered) ? summary.to_be_mastered : [];
        const tbmWords = tbm.map(x => (typeof x === 'string' ? x : x.word)).filter(Boolean);
        const teacherSet = new Set((summary?.teacher_assigned || []).map(w => w));
        const teacherFirst = []; const others = [];
        for (const w of tbmWords) { if (teacherSet.has(w)) teacherFirst.push(w); else others.push(w); }
        baseAvailable = teacherFirst.length + others.length;
      }

      // 璇嶅簱鍙ˉ榻愭暟閲忥細浣跨敤 preview count 澶ф暟鏉ヤ及璁?
      let supAvailable = 0;
      if (quickWbId) {
        try {
          setQuickMaxLoading(true);
          const token = localStorage.getItem('token');
          const res = await fetch(`/api/student/wordbooks/${quickWbId}/preview?count=9999`, { headers: { Authorization: `Bearer ${token}` } });
          const data = await res.json().catch(()=>({count:0}));
          if (res.ok) supAvailable = Number(data?.count || 0);
        } catch (_) {
          supAvailable = 0;
        } finally {
          setQuickMaxLoading(false);
        }
      }
      setQuickMax(Math.max(0, baseAvailable + supAvailable));
    };
    calcQuickMax();
  }, [showQuick, quickWbId, summary]);

  if (loading) return <div className="text-center p-10">Loading...</div>;
  if (error) return (
    <div className="text-center p-10">
      <div className="text-red-600 font-semibold">Error: {error}</div>
      {String(error || '').toLowerCase().includes('failed to fetch learning data') && (
        <div className="mt-4 inline-flex flex-col items-center gap-3 bg-yellow-50 text-yellow-900 border border-yellow-200 rounded p-4">
          <div className="text-sm">
            {t('It’s likely your last login was too long ago. Please log in again to refresh your session.')}
          </div>
          <button
            onClick={handleSessionExpired}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {t('Go to Login')}
          </button>
        </div>
      )}
    </div>
  );
  const hasTeacher = summary?.has_teacher === true;

  const handleStartLearning = async () => {
    if (!selectedWordbookId) { alert('Please select a wordbook'); return; }
    const n = parseInt(learnCount, 10) || 0;
    if (n <= 0) { alert('Please enter the number of words to learn'); return; }
    setIsAssigning(true);
    try {
      // 鍚庣缁熶竴缁勮瀛︿範璁″垝锛氬緟鎺屾彙浼樺厛 + 鎸囧畾璇嶅簱琛ラ綈
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/student/learning-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ wordbook_id: selectedWordbookId, count: n })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to generate learning plan');
      const words = Array.isArray(data?.words) ? data.words.slice(0, n) : [];
      if (words.length === 0) { alert('This wordbook has no available new words'); return; }
      // 缂撳瓨涓€娆￠瑙堢粨鏋滐紙鍙€夛級
      setPreviewWords(words);
      setPreviewTitle(data?.wordbook_title || 'Selected Wordbook');
      setPreviewMeta({ wbId: selectedWordbookId, count: n });

      const tier = selectedTier || summary?.default_tier || summary?.tier || DEFAULT_TIER;
      const params = new URLSearchParams();
      params.set('mode', 'learn');
      params.set('tier', tier);
      params.set('autostart', '1');
      params.set('focus_words', words.join(','));
      navigate(`/student/word-practice?${params.toString()}`);
    } catch (e) {
      alert(e.message || 'Operation failed');
    } finally {
      setIsAssigning(false);
    }
  };

  // 涓€閿涔狅細鍏堜娇鐢ㄢ€滃緟鎺屾彙鍒楄〃鈥濈殑鐜版湁璇嶄綔涓哄熀纭€锛堜笉鍐欏叆锛夛紝涓嶈冻閮ㄥ垎璁╃敤鎴烽€夋嫨璇嶅簱琛ラ綈锛涚‘璁ゅ紑濮嬫椂鍐嶅啓鍏?
  const handleOneClickLearn = async () => {
    try {
      const useSecret = (summary?.has_secret === true && summary?.secret_wordbook_completed !== true);
      const usedLearned = useSecret
        ? (typeof summary?.secret_today_learned === 'number' ? summary.secret_today_learned : 0)
        : (todayLearned || 0);
      const remaining = Math.max(0, (summary?.learning_goal || 0) - usedLearned);
      const tbmCount = (summary?.has_teacher === true && Array.isArray(summary?.to_be_mastered)) ? summary.to_be_mastered.length : 0;
      const desired = tbmCount + remaining;
      if (desired <= 0) { alert(t('No available learning plan: To-be-mastered is empty and today’s goal is met.')); return; }
      setIsAssigning(true);
      // Select default supplement wordbook: prefer "Custom Wordbook" (private)
      let defaultTopWb = '';
      try {
        const secret = (wordbooks || []).find(b => b && b.title === 'Custom Wordbook' && b.accessibility === 'private');
        defaultTopWb = secret?._id || (wordbooks[0]?._id || '');
      } catch(_) {
        defaultTopWb = (wordbooks[0]?._id || '');
      }
      if (!defaultTopWb && Array.isArray(wordbooks) && wordbooks.length === 0) {
        try { const t = localStorage.getItem('token'); const r = await fetch('/api/student/wordbooks', { headers: { Authorization: `Bearer ${t}` } }); if (r.ok) { const list = await r.json(); if (Array.isArray(list) && list.length>0) { defaultTopWb = list[0]._id || ''; setWordbooks(list); } } } catch(_) {}
      }
      setTopUpTarget(desired);
      setTopUpSelectedWbId(defaultTopWb);
      // 璋冪敤鍚庣缁熶竴璁″垝涓€娆″苟缂撳瓨
      if (defaultTopWb && desired > 0) {
        try {
          setTopUpLoading(true);
          const token = localStorage.getItem('token');
          const res = await fetch(`/api/student/learning-plan`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ wordbook_id: defaultTopWb, count: desired })
          });
          const data = await res.json().catch(()=>({}));
          if (res.ok) {
            setOneClickPlannedWords(Array.isArray(data?.words) ? data.words : []);
            setOneClickPlanTitle(data?.wordbook_title || 'Selected Wordbook');
            setOneClickPlanCounts({ base_count: Number(data?.base_count||0), supplement_count: Number(data?.supplement_count||0) });
          } else {
            setOneClickPlannedWords([]);
            setOneClickPlanCounts({ base_count: 0, supplement_count: 0 });
          }
        } catch(_) {
          setOneClickPlannedWords([]);
          setOneClickPlanCounts({ base_count: 0, supplement_count: 0 });
        } finally {
          setTopUpLoading(false);
        }
      }
      setOneClickTier(selectedTier || summary?.default_tier || summary?.tier || DEFAULT_TIER);
      setInitialAssignWbId(EXISTING_KEY);
      setShowOneClickPreview(false);
      // 鐜板湪鍐嶅睍绀哄脊绐楋紝宸插叿澶囪ˉ榻愰瑙堬紝閬垮厤鈥滃厛鏄剧ず鍚庤绠椻€濈殑浣撻獙
      setTopUpVisible(true);
    } catch (e) {
      alert(e.message || 'One-click learning failed');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleTopUpPreview = async (wbIdParam = null, countOverride = null) => {
    const wbId = wbIdParam || topUpSelectedWbId;
    if (!wbId) { alert('Please select a wordbook'); return; }
    try {
      setTopUpLoading(true);
      const token = localStorage.getItem('token');
      const needCount = (countOverride ?? topUpNeeded) || 0;
      if (needCount <= 0) { setTopUpLoading(false); return; }
      const res = await fetch(`/api/student/wordbooks/${wbId}/preview?count=${encodeURIComponent(needCount)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(payload?.message || 'Top-up failed');
      const newWordsRaw = Array.isArray(payload?.words) ? payload.words.slice(0, needCount) : [];
      const existingSet = new Set(topUpWords);
      const newWords = newWordsRaw.filter(w => !existingSet.has(w));
      const combined = Array.from(new Set([...(topUpWords||[]), ...newWords]));
      const stillNeed = Math.max(0, (topUpTarget || 0) - combined.length);
      setTopUpWords(combined);
      setTopUpNeeded(stillNeed);
      setTopUpPlan(prev => ({ ...prev, [wbId]: [...new Set([...(prev[wbId] || []), ...newWords])] }));
    } catch (e) {
      alert(e.message || 'Top-up failed');
    } finally {
      setTopUpLoading(false);
    }
  };

  // moved above early returns

  const handleTopUpStartNow = async () => {
    const planned = Array.isArray(oneClickPlannedWords) ? oneClickPlannedWords : [];
    if (planned.length === 0) { alert('No learnable words'); return; }
    try {
      setTopUpAssigning(true);
      const tier = oneClickTier || selectedTier || summary?.default_tier || summary?.tier || DEFAULT_TIER;
      const params = new URLSearchParams();
      params.set('mode', 'learn');
      params.set('tier', tier);
      params.set('autostart', '1');
      if (planned.length < (topUpTarget || 0)) {
        // 鍛婄煡鐢ㄦ埛鏈瀹為檯鍙涔犳暟
        try { alert(`Only ${planned.length} learnable words found; starting with available count.`); } catch {}
      }
      params.set('focus_words', planned.join(','));
      setTopUpVisible(false);
      navigate(`/student/word-practice?${params.toString()}`);
    } catch (e) {
      alert(e.message || 'Failed to start learning');
    } finally {
      setTopUpAssigning(false);
    }
  };

  // 涓€閿涔狅細寮瑰嚭闅惧害閫夋嫨
  const handleOneClickReview = () => {
    clearCustomReviewSelection();
    setReviewTier(selectedTier || summary?.default_tier || summary?.tier || DEFAULT_TIER);
    setReviewTrainingMode((current) => (
      Number(reviewModeCounts[current] || 0) > 0
        ? current
        : Number(reviewModeCounts[TRAINING_MODE_MEANING] || 0) > 0
          ? TRAINING_MODE_MEANING
          : TRAINING_MODE_COMPREHENSIVE
    ));
    setReviewBatchCount(Math.max(1, Number(reviewPlan?.default_batch_count || 1)));
    setReviewGoalDraft(Number(reviewPlan?.daily_review_goal || 10));
    setShowReviewQuick(true);
  };

  const handleStartCustomReview = ({ items, trainingMode, tier }) => {
    const stored = writeCustomReviewSelection({ items, trainingMode, tier });
    if (!stored) {
      alert(t('Custom review could not be saved. Please try again.'));
      return;
    }
    const params = new URLSearchParams();
    params.set('mode', 'review');
    params.set('tier', tier || selectedTier || summary?.default_tier || summary?.tier || DEFAULT_TIER);
    params.set('training_mode', normalizeTrainingMode(trainingMode));
    params.set('autostart', '1');
    params.set('custom_review', '1');
    setShowCustomReview(false);
    navigate(`/student/word-practice?${params.toString()}`);
  };

  const handlePreview = async () => {
    if (!selectedWordbookId) { alert('Please select a wordbook'); return; }
    const n = parseInt(learnCount, 10) || 0;
    if (n <= 0) { alert('Please enter the number of words to learn'); return; }
    setIsPreviewing(true);
    setPreviewError('');
    setPreviewWords([]);
    setPreviewTitle('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/student/wordbooks/${selectedWordbookId}/preview?count=${encodeURIComponent(n)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Preview failed');
      setPreviewWords(Array.isArray(data.words) ? data.words : []);
      setPreviewTitle(data?.wordbook_title || 'Selected Wordbook');
      setPreviewMeta({ wbId: selectedWordbookId, count: n });
    } catch (e) {
      setPreviewError(e.message || 'Preview failed');
    } finally {
      setIsPreviewing(false);
    }
  };

  return (
    <div>
      {/* Hero header */}
      <div className="rounded-2xl p-8 mb-8 bg-gradient-to-r from-indigo-50 via-white to-purple-50 border">
        {Array.isArray(invites) && invites.length > 0 && (
          <div className="mb-4 p-3 rounded-lg border bg-yellow-50 text-yellow-800">
            <div className="font-semibold mb-2">{t('You have {count} invitations from teachers', { count: invites.length })}</div>
            <div className="space-y-2">
              {invites.map(inv => (
                <div key={inv._id} className="flex items-center justify-between p-2 rounded bg-white">
                  <div>Teacher: <span data-i18n-skip>{inv.teacher?.username || '-'} {inv.teacher?.nickname ? `(${inv.teacher.nickname})` : ''}</span></div>
                  <div className="flex gap-2">
                    <button onClick={async ()=>{ try { const t = localStorage.getItem('token'); await fetch(`/api/student/invitations/${inv._id}/accept`, { method:'PUT', headers:{ 'Authorization': `Bearer ${t}`}}); setInvites(prev=>prev.filter(x=>x._id!==inv._id)); } catch {} }} className="px-3 py-1 bg-green-600 text-white rounded text-sm">Accept</button>
                    <button onClick={async ()=>{ try { const t = localStorage.getItem('token'); await fetch(`/api/student/invitations/${inv._id}/reject`, { method:'PUT', headers:{ 'Authorization': `Bearer ${t}`}}); setInvites(prev=>prev.filter(x=>x._id!==inv._id)); } catch {} }} className="px-3 py-1 bg-gray-500 text-white rounded text-sm">Decline</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Dashboard</h1>
            <p className="mt-1 text-gray-600">{t('Welcome back! Quickly view today’s goals and review status here.')}</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Current time (Asia/Shanghai)</div>
            <div className="text-lg font-semibold text-gray-800">{currentTime}</div>
          </div>
        </div>

        {/* Status panel */}
        <div className="mt-6 bg-white rounded-xl border shadow-sm p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Status</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border bg-gray-50">
              <div className="text-sm text-gray-600">{t('Today’s Status')}</div>
              <div className={`inline-flex mt-2 items-center px-2 py-0.5 rounded-full text-xs font-semibold ${(liveStats?.goal_today_met ?? uiGoalMet) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {(liveStats?.goal_today_met ?? uiGoalMet) ? 'Achieved' : 'Not achieved'}
              </div>
            </div>
            <div className="p-4 rounded-lg border bg-gray-50">
              <div className="text-sm text-gray-600">Max Streak (days)</div>
              <div className="mt-2 text-2xl font-extrabold text-gray-900">{liveStats?.max_streak_days ?? 0}</div>
            </div>
            <div className="p-4 rounded-lg border bg-gray-50">
              <div className="text-sm text-gray-600">Current streak</div>
              <div className="mt-2 text-2xl font-extrabold text-gray-900">{liveStats?.current_streak_days ?? streak}</div>
            </div>
          </div>
        </div>

        {/* To-Do: Quizzes to complete + one-click actions */}
        <div className="mt-4 bg-white rounded-xl border shadow-sm p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">To-Do</h3>
          {quizLoading ? (
            <p className="text-gray-500">{t('Loading...')}</p>
          ) : (pendingQuizzes.length > 0 || pendingPractices.length > 0) ? (
            <ul className="divide-y divide-gray-200">
              {pendingPractices.map((item) => {
                const assignment = item.assignment || {};
                const practiceLabels = {
                  dictation: 'Dictation Loop',
                  meaning_sprint: '7-Second Meaning',
                  sudden_sprint: 'Lightning Challenge',
                  pronunciation: 'Pronunciation Practice',
                  adaptive_reading: 'Adaptive Reading',
                  ielts_speaking: 'IELTS Speaking Practice',
                };
                const typeLabel = t(practiceLabels[assignment.practice_type] || 'Practice');
                const statusLabel = item.status === 'in_progress'
                  ? t('In progress')
                  : item.status === 'overdue'
                    ? t('Overdue')
                    : item.status === 'upcoming'
                      ? t('Upcoming')
                      : t('Not started');
                const disabled = isPracticeStartDisabled(item.status);
                return (
                  <li key={`practice:${assignment.id}`} className="flex items-center justify-between gap-4 py-3">
                    <div className="min-w-0">
                      <p data-i18n-skip className="truncate text-sm font-medium text-gray-900">{assignment.title || typeLabel}</p>
                      <p className="text-sm text-gray-500">
                        {typeLabel} · {statusLabel} · {assignment.practice_type === 'ielts_speaking'
                          ? (assignment.config?.question ? t('Prompt provided') : t('General speaking assessment'))
                          : t('{count} exact meanings', { count: assignment.source?.items_snapshot?.length || 0 })}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => startAssignedPractice(item)}
                      disabled={disabled || startingPracticeId === assignment.id}
                      className="inline-flex flex-none items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      {startingPracticeId === assignment.id && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                      {disabled ? t('Upcoming') : item.status === 'in_progress' ? t('Continue') : t('Start practice')}
                    </button>
                  </li>
                );
              })}
              {pendingQuizzes.map(q => (
                <li key={q.quiz_id} className="flex items-center justify-between py-3">
                  <div>
                    <p data-i18n-skip className="text-sm font-medium text-gray-900">{q.quiz_name}</p>
                    <p className="text-sm text-gray-500">{t('Quiz')}</p>
                  </div>
                  <a href={`/quiz/${q.quiz_id}`} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">{t('Start Quiz')}</a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">{t('You have completed all available quizzes. Great job!')}</p>
          )}
          {quizError && <p className="mt-3 text-sm text-rose-600">{t(quizError)}</p>}
          <div className="mt-4 flex flex-wrap gap-3">
            {(() => {
              const goal = (liveStats?.today_goal ?? summary?.learning_goal ?? 0);
              const useSecret = (summary?.has_secret === true && summary?.secret_wordbook_completed !== true);
              const learned = useSecret
                ? (typeof summary?.secret_today_learned === 'number' ? summary.secret_today_learned : 0)
                : (Number.isFinite(todayLearned) ? todayLearned : (liveStats?.today_learned ?? 0));
              const learnRemaining = Math.max(0, Number(goal) - Number(learned || 0));
              const learnDone = learnRemaining <= 0;
              const learnedToday = learned;
              return (
                <button
                  onClick={learnDone ? undefined : handleOneClickLearn}
                  disabled={isAssigning || learnDone}
                  className={`px-4 py-2 text-white rounded bg-green-600 hover:bg-green-700 disabled:bg-green-600`}
                >
                  {learnDone ? `Completed today's learning — learned ${learnedToday} words` : `One-click Learn (${learnRemaining} left)`}
                </button>
              );
            })()}
            {(() => {
              const reviewRemaining = Number(reviewPlan?.remaining_required || 0);
              const queueCount = Number(reviewPlan?.due_count || 0);
              const reviewDone = Boolean(reviewPlan?.review_complete);
              const noReview = reviewPlan?.completion_reason === 'no_review_due';
              return (
                <>
                  <button
                    onClick={queueCount > 0 ? handleOneClickReview : undefined}
                    disabled={queueCount <= 0}
                    className="px-4 py-2 text-white rounded bg-purple-600 hover:bg-purple-700 disabled:bg-green-600"
                  >
                    {noReview
                      ? t('No review due today')
                      : reviewDone
                        ? t('Daily review goal met · {count} still available', { count: queueCount })
                        : t('Review {remaining} more today · {count} in queue', { remaining: reviewRemaining, count: queueCount })}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCustomReview(true)}
                    className="px-4 py-2 rounded border border-purple-300 bg-white text-purple-700 hover:bg-purple-50"
                  >
                    {t('Custom review')}
                  </button>
                </>
              );
            })()}
          </div>
        </div>

        {/* (moved TBM section above; this placeholder removed) */}

        {/* Quick actions (card) */}
        <div className="mt-6 bg-white border rounded-xl shadow-sm p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm text-gray-600">Quick Actions</div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/student/self-check')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-indigo-200 text-indigo-700 rounded-md hover:bg-indigo-50"
              >
                <ClipboardDocumentCheckIcon className="w-5 h-5" /> Self Check
              </button>
              <button
                onClick={() => {
                  playSoundEffect('learnNowOpen', { volume: 0.78 });
                  setQuickWbId(selectedWordbookId || (wordbooks[0]?._id || ''));
                  setQuickCount(0);
                  setQuickTier(selectedTier || summary?.tier || DEFAULT_TIER);
                  setShowQuick(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                <BoltIcon className="w-5 h-5" /> Learn Now
              </button>
              <button
                onClick={() => setShowOrder(true)}
                className="px-4 py-2 bg-white border rounded-md hover:bg-gray-50"
              >
                Adjust learning order
              </button>
              <button
                onClick={() => setShowTracked(true)}
                className="px-4 py-2 bg-white border rounded-md hover:bg-gray-50"
              >
                Manage learning wordbooks
              </button>
              {hasTeacher && (
                <button
                  onClick={() => setShowTbm(v => !v)}
                  className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200"
                >
                  {showTbm ? 'Hide To-Be-Mastered' : 'Show To-Be-Mastered'}
                </button>
              )}
            </div>
          </div>
        </div>
        {hasTeacher && showTbm && Array.isArray(summary?.to_be_mastered) && summary.to_be_mastered.length > 0 && (
          <div className="mt-6 border rounded-xl bg-white shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-800">Priority wordbook</span>
              <select
                value={priorityWordbookId}
                onChange={(e)=>{ const v = e.target.value; setPriorityWordbookId(v); loadPriorityWords(v); }}
                className="p-2 border rounded bg-white text-sm min-w-[180px]"
              >
                <option value="">None</option>
                {wordbooks.map(wb => (
                  <option data-i18n-skip key={wb._id} value={wb._id}>{wb.title}</option>
                ))}
              </select>
              {priorityWordbookId && (
                <span className="text-xs text-gray-500">{priorityLoading ? 'Searching...' : `Matched ${priorityWords.length}`}</span>
              )}
            </div>
            {(() => {
              const tbm = summary?.to_be_mastered || [];
              const tset = new Set((summary?.teacher_assigned || []).map(w => w));
              let teacherList = tbm.filter(e => (e?.source === 'teacher') || tset.has(e?.word));
              let selfList = tbm.filter(e => !((e?.source === 'teacher') || tset.has(e?.word)));

              // Build priority top list (regardless of teacher/self)
              const pset = new Set(priorityWords);
              const topList = (priorityWordbookId ? tbm.filter(e => pset.has(e?.word)) : []);

              // Remove topList items from group lists to avoid duplicates
              const topSet = new Set(topList.map(x => x.word));
              teacherList = teacherList.filter(e => !topSet.has(e?.word));
              selfList = selfList.filter(e => !topSet.has(e?.word));

              // Limit lengths for display
              const topListLimited = topList.slice(0, 200);
              teacherList = teacherList.slice(0, 200);
              selfList = selfList.slice(0, 200);
              return (
                <>
                  {priorityWordbookId && topListLimited.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold text-emerald-800 mb-2">{t('Priority wordbook: {title} ({count})', { title: priorityTitle || t('Selected'), count: topListLimited.length })}</div>
                      <ul className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {topListLimited.map((item, idx) => (
                          <li key={`p-${idx}`} className="px-2 py-1 bg-white border rounded text-gray-800 flex items-center justify-between">
                            <span className="flex min-w-0 items-center gap-2"><span data-i18n-skip>{item.word}</span><TrainingModeBadge mode={getTrainingMode(item)} /></span>
                            {item.due_date && <span className="text-xs text-gray-500 ml-2">{item.due_date}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {teacherList.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold text-indigo-800 mb-2">{t('Assigned ({count})', { count: teacherList.length })}</div>
                      <ul className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {teacherList.map((item, idx) => (
                          <li key={`t-${idx}`} className="px-2 py-1 bg-white border rounded text-gray-800 flex items-center justify-between">
                            <span className="flex min-w-0 items-center gap-2"><span data-i18n-skip>{item.word}</span><TrainingModeBadge mode={getTrainingMode(item)} /></span>
                            {item.due_date && <span className="text-xs text-gray-500 ml-2">{item.due_date}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selfList.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-2">{t('Self-study ({count})', { count: selfList.length })}</div>
                      <ul className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {selfList.map((item, idx) => (
                          <li key={`s-${idx}`} className="px-2 py-1 bg-white border rounded text-gray-800 flex items-center justify-between">
                            <span className="flex min-w-0 items-center gap-2"><span data-i18n-skip>{item.word}</span><TrainingModeBadge mode={getTrainingMode(item)} /></span>
                            {item.due_date && <span className="text-xs text-gray-500 ml-2">{item.due_date}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
        {teacherAssignments.length > 0 && (
          <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded">
            <div className="font-bold text-indigo-800 mb-2">{t('Teacher-assigned meanings (priority)')}</div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm text-indigo-700">{t('Total {count}', { count: teacherAssignments.length })}</div>
              <button
                onClick={() => {
                 setTeacherLearnCount(teacherModeCounts[teacherLearnMode] || 1);
                   setTeacherLearnMode((current) => Number(teacherModeCounts[current] || 0) > 0 ? current : TRAINING_MODE_MEANING);
                   setShowTeacherLearn(true);
                }}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                {t('Learn them now')}
              </button>
            </div>
            {showTeacherLearn && (
              <div className="mt-3 p-3 bg-white border rounded">
                <div className="text-sm font-medium text-gray-800 mb-2">{t('Choose difficulty, training mode, and amount')}</div>
                <TrainingModeSelector
                  value={teacherLearnMode}
                  onChange={(next) => {
                    setTeacherLearnMode(next);
                    setTeacherLearnCount((current) => normalizeRequestedCount(
                      current,
                      teacherModeCounts[next] || 1,
                    ));
                  }}
                  counts={teacherModeCounts}
                  showCounts
                  legend="Learning mode"
                  name="dashboard-learning-mode"
                  className="mb-3"
                />
                <div className="flex items-center gap-3 flex-wrap">
                  <select
                    value={teacherLearnTier}
                    onChange={(e)=>setTeacherLearnTier(e.target.value)}
                    className="p-2 border rounded bg-gray-50 text-sm"
                  >
                    {availableTiers.map(t => (
                      <option key={t} value={t}>{labelByTier[t] || t}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <span>{t('Number of Words')}</span>
                    <input
                      type="number"
                      min="1"
                       max={Math.max(1, teacherModeCounts[teacherLearnMode] || 1)}
                      value={teacherLearnCount}
                      onChange={(event) => {
                        setTeacherLearnCount(event.target.value);
                      }}
                      onFocus={(event) => event.currentTarget.select()}
                      onBlur={() => setTeacherLearnCount((current) => normalizeRequestedCount(
                        current,
                        teacherModeCounts[teacherLearnMode] || 1,
                      ))}
                      className="w-24 p-2 border rounded bg-gray-50 text-sm"
                    />
                     <span className="text-gray-500">/ {teacherModeCounts[teacherLearnMode] || 0}</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setTeacherLearnCount(teacherModeCounts[teacherLearnMode] || 1)}
                    className="px-3 py-2 text-sm text-indigo-700 border border-indigo-200 rounded hover:bg-indigo-50"
                  >
                    {t('All')}
                  </button>
                  <button
                    onClick={() => {
                      try {
                        const assignments = selectTeacherAssignments(
                           teacherAssignments.filter((item) => getTrainingMode(item) === teacherLearnMode),
                           normalizeRequestedCount(
                             teacherLearnCount,
                             teacherModeCounts[teacherLearnMode] || 1,
                           ),
                        );
                        if (assignments.length === 0) { alert('No teacher-assigned meanings to learn'); return; }
                        const params = new URLSearchParams();
                        params.set('mode', 'learn');
                        params.set('tier', teacherLearnTier || (availableTiers[0] || DEFAULT_TIER));
                        params.set('autostart', '1');
                         params.set('training_mode', normalizeTrainingMode(teacherLearnMode));
                        params.set('focus_words', [...new Set(assignments.map((item) => item.word))].join(','));
                        params.set('focus_assignments', 'session');
                        sessionStorage.setItem('word_practice_focus_assignments', JSON.stringify(assignments));
                        navigate(`/student/word-practice?${params.toString()}`);
                      } catch (e) {
                        alert('Failed to start learning');
                      }
                    }}
                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Start learning
                  </button>
                  <button onClick={()=>setShowTeacherLearn(false)} className="px-3 py-1.5 text-sm bg-gray-100 border rounded hover:bg-gray-200">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 閽堝鎬у涔犻潰鏉垮凡鍙栨秷锛岀浉鍏冲叆鍙ｇЩ鍔ㄨ嚦涓婃柟鈥滃涔犻潰鏉库€濈殑蹇€熸搷浣?*/}
      {showOrder && (
        <LearningOrderModal
          isOpen={showOrder}
          onClose={async (saved) => { setShowOrder(false); if (saved) { await fetchSummary(); } }}
          wordbooks={wordbooks}
        />
      )}
      {showTracked && (
        <ManageTrackedWordbooksModal
          isOpen={showTracked}
          onClose={async (saved)=>{
            setShowTracked(false);
            if (saved) {
              // 鍙樻洿杩借釜璇嶅簱鍚庯紝绔嬪嵆鍒锋柊璇嶅簱鍒楄〃浠ョ敓鏁?
              try {
                const token = localStorage.getItem('token');
                const [wordbooksRes, trackedRes] = await Promise.all([
                  fetch('/api/student/wordbooks', { headers: { Authorization: `Bearer ${token}` } }),
                  fetch('/api/student/tracked-wordbooks', { headers: { Authorization: `Bearer ${token}` } }),
                ]);
                if (wordbooksRes.ok) {
                  const list = await wordbooksRes.json();
                  let books = Array.isArray(list) ? list : [];
                  if (trackedRes.ok) {
                    const tr = await trackedRes.json().catch(()=>({ ids: [] }));
                    const ids = Array.isArray(tr.ids) ? tr.ids : [];
                    if (ids.length > 0) {
                      // 涓庡垵濮嬪姞杞戒繚鎸佷竴鑷达細淇濈暀绉佹湁璇嶅簱 + 杩借釜鐨勫叕寮€璇嶅簱
                      const priv = books.filter(b => (b && b.accessibility === 'private'));
                      const tracked = books.filter(b => ids.includes(b._id));
                      const merged = {};
                      for (const b of [...priv, ...tracked]) { if (b && b._id) merged[b._id] = b; }
                      books = Object.values(merged);
                    }
                  }
                  setWordbooks(books);
                  if (!selectedWordbookId && books.length > 0) setSelectedWordbookId(books[0]._id);
                }
              } catch (_) {}
            }
          }}
        />
      )}

      {/* onboarding 绉婚櫎 */}

      {showQuick && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-900">Start Learning</h3>
            <p className="text-gray-600 mt-1 text-sm">Choose wordbook, count, and difficulty.</p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Choose wordbook (for supplement)</label>
                <select value={quickWbId} onChange={(e)=>setQuickWbId(e.target.value)} className="w-full p-3 border rounded-md bg-gray-50">
                  {wordbooks.map(wb => (
                    <option data-i18n-skip key={wb._id} value={wb._id}>{wb.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('Count (max: {count})', { count: quickMaxLoading ? t('Calculating...') : quickMax })}</label>
                <input type="number" min="0" max={Math.max(0, quickMax)} value={Number.isFinite(quickCount)?quickCount:0} onChange={(e)=> {
                  const v = parseInt(e.target.value||'0',10);
                  const clamped = Math.max(0, Math.min(Number.isFinite(quickMax)?quickMax:200, Number.isFinite(v)?v:0));
                  setQuickCount(clamped);
                }} className="w-full p-3 border rounded-md bg-gray-50" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Choose difficulty</label>
                <select value={quickTier} onChange={(e)=>setQuickTier(e.target.value)} className="w-full p-3 border rounded-md bg-gray-50">
                  {availableTiers.map(t => (
                    <option key={t} value={t}>{labelByTier[t]}</option>
                  ))}
                </select>
              </div>
              <div>
                <button
                  type="button"
                  onClick={async ()=>{
                    try {
                      const n = parseInt(quickCount, 10) || 0;
                      if (n <= 0) { alert('Please enter the number of words to learn'); return; }
                      if (!quickWbId) { alert('Please select a wordbook'); return; }
                      setIsPreviewing(true);
                      setPreviewError('');
                      setPreviewWords([]);
                      setPreviewTitle('');
                      const token = localStorage.getItem('token');
                      const res = await fetch(`/api/student/learning-plan`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ wordbook_id: quickWbId, count: n })
                      });
                      const data = await res.json().catch(()=>({}));
                      if (!res.ok) throw new Error(data?.message || 'Preview failed');
                      setPreviewWords(Array.isArray(data?.words) ? data.words : []);
                      setPreviewTitle(data?.wordbook_title || 'Selected Wordbook');
                      setPreviewMeta({ wbId: quickWbId, count: n });
                    } catch (e) {
                      setPreviewError(e?.message || 'Preview failed');
                    } finally {
                      setIsPreviewing(false);
                    }
                  }}
                  className="w-full px-3 py-2 text-sm bg-white border rounded hover:bg-gray-50"
                >
                  Preview words to learn
                </button>
              </div>
              {(() => {
                const match = previewWords && previewWords.length > 0 && previewMeta.wbId === quickWbId && Number(previewMeta.count) === Number(quickCount || 0);
                if (!match) return null;
                return (
                  <div className="p-3 bg-gray-50 border rounded max-h-40 overflow-y-auto text-sm text-gray-800">
                    {previewWords.join(', ')}
                  </div>
                );
              })()}
              {(() => {
                // Summary: TBM first then supplement from selected wordbook (only if connected to teacher)
                const teacherConnected = summary?.has_teacher === true;
                let baseCount = 0;
                if (teacherConnected) {
                  const tbm = Array.isArray(summary?.to_be_mastered) ? summary.to_be_mastered : [];
                  const tbmWords = tbm.map(x => (typeof x === 'string' ? x : x.word)).filter(Boolean);
                  const teacherSet = new Set((summary?.teacher_assigned || []).map(w => w));
                  const teacherFirst = []; const others = [];
                  for (const w of tbmWords) { if (teacherSet.has(w)) teacherFirst.push(w); else others.push(w); }
                  const baseOrdered = [...teacherFirst, ...others];
                  baseCount = Math.min(quickCount || 0, baseOrdered.length);
                }
                const supCount = Math.max(0, (quickCount || 0) - baseCount);
                const wb = wordbooks.find(w => w._id === quickWbId);
                return (
                  <div className="text-xs text-gray-600 bg-gray-50 border rounded p-2">
                    {teacherConnected && baseCount > 0 ? (
                      <>You will learn {baseCount} to-be-mastered words{supCount > 0 && <>, and {supCount} from <span data-i18n-skip>{wb?.title || 'Selected Wordbook'}</span></>}</>
                    ) : (
                      <>You will learn {supCount} from <span data-i18n-skip>{wb?.title || 'Selected Wordbook'}</span></>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button onClick={()=>setShowQuick(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button
                disabled={quickAssigning || (()=>{ const tbm=Array.isArray(summary?.to_be_mastered)?summary.to_be_mastered:[]; const tw=tbm.map(x=> (typeof x==='string'?x:x.word)).filter(Boolean); const ts=new Set((summary?.teacher_assigned||[]).map(w=>w)); const tf=[]; const ot=[]; for (const w of tw){ if (ts.has(w)) tf.push(w); else ot.push(w);} const bo=[...tf,...ot]; const supNeeded=Math.max(0,(quickCount||0)-bo.length); return (supNeeded>0 && !quickWbId); })()}
                onClick={async ()=>{
                  const n = parseInt(quickCount, 10) || 0;
                  if (n <= 0) { alert('Please enter the number of words to learn'); return; }
                  try {
                    playSoundEffect('modeEnter', { volume: 0.82 });
                    setQuickAssigning(true);
                    // 1) 鍏堜粠寰呮帉鎻″垪琛ㄥ彇 n 涓紙鑰佸笀甯冪疆浼樺厛锛?
                    const tbm = Array.isArray(summary?.to_be_mastered) ? summary.to_be_mastered : [];
                    const tbmWords = tbm.map(x => (typeof x === 'string' ? x : x.word)).filter(Boolean);
                    const teacherSet = new Set((summary?.teacher_assigned || []).map(w => w));
                    const teacherFirst = [];
                    const others = [];
                    for (const w of tbmWords) { if (teacherSet.has(w)) teacherFirst.push(w); else others.push(w); }
                    const baseOrdered = [...teacherFirst, ...others];
                    const got = baseOrdered.slice(0, n);
                    let combined = [...got];
                    const need = Math.max(0, n - got.length);
                    // 2) 鑻ヤ笉澶燂紝浜ょ敱鍚庣缁熶竴鐢熸垚璁″垝锛堝緟鎺屾彙浼樺厛 + 鎸囧畾璇嶅簱琛ラ綈锛?
                    if (need > 0) {
                      if (!quickWbId) { alert('Please select a wordbook'); return; }
                      const token = localStorage.getItem('token');
                      const res = await fetch(`/api/student/learning-plan`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ wordbook_id: quickWbId, count: n })
                      });
                      const payload = await res.json().catch(()=>({}));
                      if (!res.ok) throw new Error(payload?.message || 'Failed to generate learning plan');
                      const planned = Array.isArray(payload?.words) ? payload.words.slice(0, n) : [];
                      // 璁″垝宸插寘鍚緟鎺屾彙浼樺厛閫昏緫锛岃繖閲岀洿鎺ヤ娇鐢ㄨ鍒掕鐩栫粍鍚?
                      combined = planned;
                      if (planned.length < n) {
                        try { alert(`Exceeds available new words. You can learn at most ${planned.length} this time. Starting with available count.`); } catch {}
                      }
                      if (combined.length === 0) { alert('This wordbook has no available new words'); return; }
                    }
                    // 3) 璺宠浆寮€缁?
                    const params = new URLSearchParams();
                    params.set('mode', 'learn');
                    params.set('tier', quickTier || summary?.tier || DEFAULT_TIER);
                    params.set('autostart', '1');
                    params.set('focus_words', combined.join(','));
                    setShowQuick(false);
                    navigate(`/student/word-practice?${params.toString()}`);
                  } catch (e) {
                    alert(e?.message || 'Operation failed');
                  } finally {
                    setQuickAssigning(false);
                  }
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded disabled:bg-gray-300"
              >{quickAssigning ? 'Preparing...' : 'Start learning'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 椤甸潰澶栭瑙堝凡绉婚櫎锛涢瑙堜粎鍦ㄥ脊绐楀唴鏄剧ず */}

      {showReviewQuick && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-900">{t('Start Review')}</h3>
            <p className="text-gray-600 mt-1 text-sm">
              {t('{done}/{goal} meanings completed', {
                done: reviewPlan?.completed_today || 0,
                goal: reviewPlan?.required_today || 0,
              })} · {t('{count} meanings in review queue', { count: reviewModeCounts[reviewTrainingMode] || 0 })}
            </p>
            <TrainingModeSelector
              value={reviewTrainingMode}
              onChange={setReviewTrainingMode}
              counts={reviewModeCounts}
              showCounts
              legend="Review mode"
              name="dashboard-review-mode"
              className="mt-5"
            />
            <div className="mt-5 rounded-md border bg-purple-50 p-4">
              <label className="block text-sm font-semibold text-gray-800 mb-2">{t('Daily review goal')}</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={reviewGoalDraft}
                  onChange={(event) => setReviewGoalDraft(event.target.value)}
                  className="min-w-0 flex-1 p-2 border rounded bg-white"
                />
                <button
                  type="button"
                  disabled={reviewGoalSaving}
                  onClick={async () => {
                    const goal = Number.parseInt(reviewGoalDraft, 10);
                    if (!Number.isFinite(goal) || goal < 1 || goal > 500) {
                      alert(t('Enter a daily goal between 1 and 500.'));
                      return;
                    }
                    try {
                      setReviewGoalSaving(true);
                      const token = localStorage.getItem('token');
                      const response = await fetch('/api/student/review-goal', {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ goal }),
                      });
                      const payload = await response.json().catch(() => ({}));
                      if (!response.ok) throw new Error(payload?.message || t('Could not save the review goal.'));
                      setReviewPlan(payload);
                      setReviewBatchCount(Math.max(1, Number(payload.default_batch_count || 1)));
                    } catch (error) {
                      alert(error.message || t('Could not save the review goal.'));
                    } finally {
                      setReviewGoalSaving(false);
                    }
                  }}
                  className="px-3 py-2 bg-white border border-purple-300 text-purple-700 rounded disabled:opacity-60"
                >
                  {t('Save')}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-600">{t('Lower goals start tomorrow. A higher goal can apply today.')}</p>
            </div>
            <div className="mt-4">
              <label className="block text-sm text-gray-600 mb-1">{t('Meanings in this review')}</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max={Math.max(1, Number(reviewModeCounts[reviewTrainingMode] || 1))}
                  value={reviewBatchCount}
                  onChange={(event) => {
                    const value = Number.parseInt(event.target.value, 10);
                    setReviewBatchCount(Number.isFinite(value) ? value : 1);
                  }}
                  className="min-w-0 flex-1 p-3 border rounded-md bg-gray-50"
                />
                <span className="text-sm text-gray-500">/ {reviewModeCounts[reviewTrainingMode] || 0}</span>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm text-gray-600 mb-1">{t('Choose difficulty')}</label>
              <select value={reviewTier} onChange={(e)=>setReviewTier(e.target.value)} className="w-full p-3 border rounded-md bg-gray-50">
                {availableTiers.map(t => <option key={t} value={t}>{labelByTier[t]}</option>)}
              </select>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button onClick={()=>setShowReviewQuick(false)} className="px-4 py-2 bg-gray-200 rounded">{t('Cancel')}</button>
              <button onClick={()=>{
                const max = Number(reviewModeCounts[reviewTrainingMode] || 0);
                const count = Math.max(1, Math.min(max, Number.parseInt(reviewBatchCount, 10) || 1));
                if (max <= 0) return;
                clearCustomReviewSelection();
                const params = new URLSearchParams();
                params.set('mode','review');
                params.set('tier', reviewTier || summary?.tier || DEFAULT_TIER);
                params.set('review_count', String(count));
                params.set('training_mode', normalizeTrainingMode(reviewTrainingMode));
                params.set('autostart','1');
                setShowReviewQuick(false);
                navigate(`/student/word-practice?${params.toString()}`);
              }} disabled={Number(reviewModeCounts[reviewTrainingMode] || 0) <= 0} className="px-4 py-2 bg-purple-600 text-white rounded disabled:cursor-not-allowed disabled:bg-gray-300">{t('Start Review')}</button>
            </div>
          </div>
        </div>
      )}

      <CustomReviewSelector
        open={showCustomReview}
        onClose={() => setShowCustomReview(false)}
        onStart={handleStartCustomReview}
        availableTiers={availableTiers}
        initialTier={reviewTier || selectedTier || summary?.default_tier || summary?.tier || DEFAULT_TIER}
        initialTrainingMode={reviewTrainingMode}
      />

      {topUpVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
            <h3 className="text-xl font-bold text-gray-900">Start Learning</h3>
            <p className="text-gray-600 mt-2 text-sm">
              You will learn {oneClickPlanCounts.base_count} to-be-mastered words{oneClickPlanCounts.supplement_count > 0 && <>, and {oneClickPlanCounts.supplement_count} from <span data-i18n-skip>{(wordbooks.find(w=>w._id===topUpSelectedWbId)?.title) || oneClickPlanTitle || 'Selected Wordbook'}</span></>}.
            </p>
            <div className="mt-4 grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Choose difficulty</label>
                <select
                  value={oneClickTier}
                  onChange={(e)=>setOneClickTier(e.target.value)}
                  className="w-full p-3 border rounded-md bg-gray-50"
                >
                  {availableTiers.map(t => (
                    <option key={t} value={t}>{labelByTier[t]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Choose wordbook to supplement (if needed)</label>
                <select
                  value={topUpSelectedWbId}
                  onChange={(e)=>setTopUpSelectedWbId(e.target.value)}
                  className="w-full p-3 border rounded-md bg-gray-50"
                >
                  {wordbooks.map(wb => (
                    <option data-i18n-skip key={wb._id} value={wb._id}>{wb.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => { setShowOneClickPreview(true); }}
                  className="w-full px-3 py-2 text-sm bg-white border rounded hover:bg-gray-50"
                  disabled={topUpLoading || (oneClickPlannedWords||[]).length===0}
                >
                  Preview words to learn
                </button>
                {topUpLoading && <div className="text-xs text-gray-500 mt-1">{t('Preparing plan…')}</div>}
              </div>
              {showOneClickPreview && Array.isArray(oneClickPlannedWords) && oneClickPlannedWords.length > 0 && (
                <div className="p-3 bg-gray-50 border rounded max-h-40 overflow-y-auto text-sm text-gray-800">
                  {oneClickPlannedWords.join(', ')}
                </div>
              )}
            </div>
            <div className="mt-6 grid grid-cols-1 gap-3">
              <button onClick={handleTopUpStartNow} disabled={topUpAssigning} className="px-4 py-2 bg-green-600 text-white rounded-md">{topUpAssigning ? 'Starting...' : 'Confirm start'}</button>
            </div>
            <button onClick={()=>setTopUpVisible(false)} className="mt-3 w-full text-sm text-gray-500 hover:text-gray-700">Cancel and go back</button>
          </div>
        </div>
      )}

    </div>
  );
};

export default StudentDashboard;
